import { useEffect, useState } from "react";
import { format, addDays } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, History, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props { groupId: string; groupName: string; }
interface Grant {
  id: string;
  granted_to: string;
  attendance_date: string;
  reason: string;
  expires_at: string;
  consumed_at: string | null;
  revoked_at: string | null;
  granted_at: string;
  user_label?: string;
}
interface UserOption { user_id: string; label: string; }

export function BackdateGrantsDialog({ groupId, groupName }: Props) {
  const [open, setOpen] = useState(false);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [grantedTo, setGrantedTo] = useState<string>("");
  const [date, setDate] = useState<Date | undefined>();
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState("24");
  const [saving, setSaving] = useState(false);

  async function loadGrants() {
    const { data } = await supabase
      .from("rotation_backdate_grants" as any)
      .select("*")
      .eq("field_group_id", groupId)
      .order("granted_at", { ascending: false });
    const rows = ((data ?? []) as any[]) as Grant[];
    // Best-effort label lookup
    const ids = Array.from(new Set(rows.map((r) => r.granted_to)));
    if (ids.length) {
      const { data: profs } = await supabase.from("profiles").select("user_id, full_name").in("user_id", ids);
      const map = new Map((profs ?? []).map((p: any) => [p.user_id, p.full_name]));
      rows.forEach((r) => { r.user_label = map.get(r.granted_to) ?? r.granted_to.slice(0, 8); });
    }
    setGrants(rows);
  }

  async function loadUsers() {
    // Staff: anyone with a non-student role
    const { data: ur } = await supabase.from("user_roles").select("user_id, role");
    const staffIds = Array.from(new Set(((ur ?? []) as any[]).filter((r) => r.role !== "student").map((r) => r.user_id)));
    if (!staffIds.length) { setUsers([]); return; }
    const { data: profs } = await supabase.from("profiles").select("user_id, full_name").in("user_id", staffIds);
    const opts = ((profs ?? []) as any[]).map((p) => ({ user_id: p.user_id, label: p.full_name ?? p.user_id.slice(0, 8) }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    setUsers(opts);
  }

  useEffect(() => { if (open) { loadGrants(); loadUsers(); } }, [open, groupId]);

  async function createGrant() {
    if (!grantedTo || !date) { toast.error("Pick a user and a date"); return; }
    if (reason.trim().length < 10) { toast.error("Reason must be at least 10 characters"); return; }
    const h = Math.max(1, Math.min(168, Number(hours) || 24));
    const expires = new Date(Date.now() + h * 3600_000).toISOString();
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("rotation_backdate_grants" as any).insert({
      field_group_id: groupId,
      granted_to: grantedTo,
      attendance_date: format(date, "yyyy-MM-dd"),
      reason: reason.trim(),
      expires_at: expires,
      granted_by: u.user?.id,
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Backdate grant issued");
    setGrantedTo(""); setDate(undefined); setReason(""); setHours("24");
    loadGrants();
  }

  async function revoke(g: Grant) {
    const { error } = await supabase.from("rotation_backdate_grants" as any)
      .update({ revoked_at: new Date().toISOString() }).eq("id", g.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Grant revoked");
    loadGrants();
  }

  function statusOf(g: Grant): { label: string; variant: "default" | "secondary" | "outline" | "destructive" } {
    if (g.revoked_at) return { label: "Revoked", variant: "destructive" };
    if (g.consumed_at) return { label: "Used", variant: "secondary" };
    if (new Date(g.expires_at) <= new Date()) return { label: "Expired", variant: "outline" };
    return { label: "Active", variant: "default" };
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Backdating grants">
          <History className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Backdating — {groupName}</DialogTitle>
          <DialogDescription>
            Authorise a specific staff member to record rotation attendance for a past date. Expires automatically; one-shot per scanner per date.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border rounded-md p-3">
          <div className="space-y-1">
            <Label className="text-xs">Staff member</Label>
            <Select value={grantedTo} onValueChange={setGrantedTo}>
              <SelectTrigger><SelectValue placeholder="Pick staff…" /></SelectTrigger>
              <SelectContent position="popper">
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Backdate to</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PP") : "Pick past date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d >= new Date(new Date().setHours(0,0,0,0)) || d < addDays(new Date(), -90)}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs">Reason (required, min 10 chars)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Network outage on site prevented sync" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expires in (hours, max 168)</Label>
            <Input type="number" min={1} max={168} value={hours} onChange={(e) => setHours(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button onClick={createGrant} disabled={saving} className="w-full">Issue grant</Button>
          </div>
        </div>

        <div className="border rounded-md max-h-64 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grants.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-4">No grants yet.</TableCell></TableRow>
              ) : grants.map((g) => {
                const s = statusOf(g);
                return (
                  <TableRow key={g.id}>
                    <TableCell className="text-xs">{g.user_label}</TableCell>
                    <TableCell className="text-xs">{format(new Date(g.attendance_date), "PP")}</TableCell>
                    <TableCell className="text-xs">{format(new Date(g.expires_at), "PP p")}</TableCell>
                    <TableCell><Badge variant={s.variant}>{s.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      {!g.revoked_at && !g.consumed_at && new Date(g.expires_at) > new Date() && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => revoke(g)} title="Revoke">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
